import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";

/**
 * Shared, professional page header for all portal pages.
 *
 * Renders (top → bottom):
 *   1. Breadcrumb trail  (Dashboard › … › <title>)
 *   2. Circular back button + bold title + optional subtitle, with an
 *      optional right-aligned actions area
 *   3. A divider, then any `children` (e.g. a search / filter row)
 *
 * IMPORTANT: the breadcrumb uses a <div role="navigation"> — NOT a <nav> —
 * because lightMode.css forces every <nav> to a dark navy background, which
 * would put an ugly black bar behind the breadcrumb.
 *
 * Props:
 *   title       string                         — page title (required)
 *   subtitle    string                         — one-line description under the title
 *   backTo      string   (default Dashboard)   — where the back button navigates; pass null to hide it
 *   breadcrumbs [{ label, to? }]               — trail BEFORE the current title (default: Dashboard)
 *   actions     ReactNode                      — right-aligned buttons/icons
 *   children    ReactNode                      — rendered below the divider (search row, tabs, etc.)
 *   className   string                         — extra classes on the wrapper
 */
export default function PageHeader({
  title,
  subtitle,
  backTo = "/portal/Dashboard",
  breadcrumbs = [{ label: "Dashboard", to: "/portal/Dashboard" }],
  actions = null,
  children,
  className = "",
}) {
  return (
    <div className={`relative ${className}`}>
      

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          {backTo && (
            <Link
              to={backTo}
              aria-label="Go back"
              title="Go back"
              className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
          )}
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:gap-3">
            {actions}
          </div>
        )}
      </div>

      <div className="mt-5 border-b border-slate-200"></div>

      {children}
    </div>
  );
}
